
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { APIContext } from "../../../connector";
import { api_url, ws_url } from "../../../constants";
import { chatApi, getErrorMessage } from "../../../api";
import { useToast } from "../../../hooks/useToast";
import ProfilePlaceholder from '../../../static/images/user.svg';
import { appendChatToIndexedDB, getAllChatsFromIndexedDB, updateChatHistoryInIndexedDB } from "../../../utils/chatHelper";
import { getImageUrl } from "../../../utils/helpers";
import useReliableWebSocket from "../../../utils/useReliableWebSocket";
import classes from "./Chat.module.scss";

const ChatPage = () => {

  const { activeTripData, userData } = useContext(APIContext);
  const { openToast } = useToast();
  const tripId = activeTripData?.trip_id;

  const tripUrl = `${ws_url}/ws/trip/${tripId}/`;

  // Determine the sender and receiver IDs based on trip data
  const senderID = userData?.user_id;
  let receiverID, name, profilePicture;
  if (userData?.user_id === activeTripData?.user) {
    receiverID = activeTripData?.driver;
    name = activeTripData?.driver_details?.name || "Unknown";
    profilePicture = activeTripData?.driver_details?.profile_picture ? getImageUrl(activeTripData.driver_details.profile_picture, api_url) : ProfilePlaceholder;
  } else {
    receiverID = activeTripData?.user
    name = activeTripData?.user_details?.name || "Client";
    profilePicture = ProfilePlaceholder;
  }

  const loadMessages = useCallback(() => {
    getAllChatsFromIndexedDB().then((chats) => {
      setMessages(chats);
    });
  }, []);

  const { sendJsonMessage: sendChatMessage, lastJsonMessage: chatMessage } = useReliableWebSocket(tripUrl, {
    share: true,
    shouldReconnect: (closeEvent) => true,
    onError: (error) => {
      console.error("Trip WebSocket error:", error);
    },
    retryOnError: false,
    persistenceKey: `chat_queue_${tripId}`,
  });

  const [message, setMessage] = useState("")
  const [messages, setMessages] = useState([]);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  // if an image is selected, it will be displayed in a modal
  const [selectedImage, setSelectedImage] = useState(null);

  // Scroll to bottom helper
  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" })
  }

  const handleOnClose = () => {
    // handle any cleanup if necessary
    window.history.back();
  }

  // Smart scroll: check if user is near bottom before update
  const isNearBottom = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return true; // Default to true if ref not ready
    const threshold = 100; // pixels from bottom to be considered "at bottom"
    const position = container.scrollHeight - container.scrollTop - container.clientHeight;
    return position < threshold;
  }, []);

  const sendMessagePayload = async ({ text = "", image = null }) => {
    if (!text.trim() && !image) return;

    const newMessage = {
      text: text.trim(),
      image: image,
      time: new Date().toISOString(),
      from: senderID,
      to: receiverID,
    }

    // Optimistic update
    setMessages((prevMessages) => [...prevMessages, newMessage]);

    // Persist immediately
    appendChatToIndexedDB(newMessage).catch(console.error);

    // Send to server
    sendChatMessage({
      event: "chat_message",
      data: JSON.stringify(newMessage),
      url: "/chat"
    })

    // Always scroll to bottom when sending
    setTimeout(() => scrollToBottom(), 100);
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (message.trim()) {
      await sendMessagePayload({ text: message.trim() });
      setMessage("")
    }
  }

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    try {
      // TODO: Perhaps add loading state could be added here
      const data = await chatApi.uploadImage(formData);

      if (data.image) {
        await sendMessagePayload({ image: data.image });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      const message = getErrorMessage(error);
      openToast(message || "Failed to upload image. Please try again.", 5000, "danger");
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Load initial chats
  useEffect(() => {
    loadMessages();
    // Initial scroll to bottom without animation
    setTimeout(() => scrollToBottom(false), 100);
  }, [loadMessages]);

  useEffect(() => {
    const wasNearBottom = isNearBottom();
    if (wasNearBottom) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [isNearBottom, messages]);

  // Handle incoming messages
  useEffect(() => {
    if (!chatMessage) return;

    switch (chatMessage.type) {
      case "chat_message":
        const msg = chatMessage?.data;
        if (!msg) return;

        let messageData;
        try {
          messageData = typeof msg === 'string' ? JSON.parse(msg) : msg;
        } catch (e) {
          console.warn("Failed to parse chat message", e);
          return;
        }

        // If message is from someone else, add it
        if (messageData.from !== senderID) {
          // Use a function to avoid race conditions
          setMessages((prevMessages) => {
            return [...prevMessages, messageData].sort((a, b) => new Date(a.time) - new Date(b.time));
          });

          appendChatToIndexedDB(messageData).catch(console.error);
        }
        break;
      case "chat_history":
        const history = chatMessage?.data;
        if (!history) return;


        // merge history with current messages while avoiding duplicates
        setMessages((prevMessages) => {
          const allMessages = [...history, ...prevMessages];
          const seenKeys = new Set();
          const uniqueMessages = [];

          for (const msg of allMessages) {
            // Create a unique key for deduplication
            // Handling both text and image messages
            const key = `${msg.time}-${msg.from}-${msg.text || ""}-${msg.image || ""}`;

            if (!seenKeys.has(key)) {
              seenKeys.add(key);
              uniqueMessages.push(msg);
            }
          }

          // Sort by time to ensure correct order
          return uniqueMessages.sort((a, b) => new Date(a.time) - new Date(b.time));
        });

        updateChatHistoryInIndexedDB(chatMessage?.data).catch(console.error);
        break;
      default:
        break;
    }
  }, [chatMessage, isNearBottom, senderID]);

  return (
    <div className={classes.chatContainer}>
      <div className={classes.chatHeader}>
        <button onClick={handleOnClose} className={classes.backButton} aria-label="Go back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
        </button>
        <div className={classes.userInfo}>
          <div className={classes.profilePicture}>
            <img src={profilePicture} alt={name || "User"} onError={(e) => { e.target.src = ProfilePlaceholder }} />
          </div>
          <div className={classes.userName}>{name}</div>
        </div>
      </div>

      <div className={classes.chatMessages} ref={chatContainerRef}>
        {messages.map((msg, index) => {
          const date = new Date(msg.time);
          const formattedTime = isNaN(date.getTime()) ? "" : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

          // Key: using time+from+index as simple unique key
          const key = `${msg.time}-${msg.from}-${index}`;

          return (
            <div key={key} className={`${classes.message} ${classes[msg.from === senderID ? "me" : "them"]}`}>
              <div className={classes.messageContent}>
                {msg.image && (
                  <img
                    src={getImageUrl(msg.image, api_url)}
                    alt="Sent attachment"
                    className={classes.messageImage}
                    onClick={() => setSelectedImage(getImageUrl(msg.image, api_url))}
                  />
                )}
                {msg.text && <div>{msg.text}</div>}
              </div>
              <div className={classes.messageTime}>{formattedTime}</div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <form className={classes.messageInputContainer} onSubmit={handleSendMessage}>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/*"
          onChange={handleFileSelect}
        />
        <button
          type="button"
          className={classes.uploadButton}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Upload image"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
        </button>
        <input
          type="text"
          placeholder="Type a message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={classes.messageInput}
          autoComplete="off"
        />
        <button type="submit" className={classes.sendButton} disabled={!message.trim()} aria-label="Send message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
        </button>
      </form>

      {selectedImage && (
        <div className={classes.imageModalOverlay} onClick={() => setSelectedImage(null)}>
          <button
            className={classes.closeModalButton}
            onClick={() => setSelectedImage(null)}
            aria-label="Close image preview"
          >
            ×
          </button>

          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', height: '100%' }}>
            <TransformWrapper
              initialScale={1}
              minScale={0.8}
              maxScale={4}
              centerOnInit={true}
              wheel={{ step: 0.2 }}
              doubleClick={{ step: 1 }}
            >
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
              >
                <img
                  src={selectedImage}
                  alt="Full size"
                  style={{ maxWidth: "100%", maxHeight: "100%" }}
                />
              </TransformComponent>
            </TransformWrapper>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatPage;
